// Markdown body for the post. Metadata lives in src/blog/posts.json.
const body = `
Measure a necklace you already wear and like, flat on a table, from one end of the clasp to the other. That number in inches or centimetres is the only one that matters. Everything below is about turning it into the right choice when you are buying something you cannot try on.

## The standard lengths, and where they sit

Measured on an average adult neck. A larger neck moves each length up the body by an inch or so.

| Length | Name | Where it sits |
| --- | --- | --- |
| 14 in / 36 cm | Collar | Tight around the middle of the neck |
| 16 in / 41 cm | Choker | At the base of the neck |
| 18 in / 46 cm | Princess | Just below the collarbone. The most common length |
| 20 to 22 in / 51 to 56 cm | Matinee | Top of the bust |
| 24 to 30 in / 61 to 76 cm | Opera | At or just below the bust |
| 32 in and longer / 81 cm+ | Rope or sautoir | Below the bust, often doubled |

Two practical notes. A pendant hangs **below** the chain, so a heavy drop on an 18 inch chain sits where a 20 inch chain would sit empty. And antique chains are frequently longer than modern ones, because long guard chains and sautoirs were the fashion.

## Choosing a length without trying it on

- **For a pendant you already own**, match the chain to where you want the pendant to land, then subtract the length of the pendant and its bail.
- **For layering**, leave at least 2 inches between chains so they do not tangle and each one reads separately.
- **With a high neckline**, go shorter than instinct suggests. With an open neckline, longer.
- **If you are unsure**, 18 inches is the safe answer for most people and most pendants.

Use a piece of string or a tape measure held loosely around your neck at the height you want, then measure the string flat. Do not measure your neck and add a guess.

## Chain types, and which ones actually last

The name tells you how the links are made, and that determines strength, drape and how easily it kinks.

**Cable.** Plain oval links, each at ninety degrees to the last. Simple, strong, easy to repair. The default for a reason.

**Belcher, also called rolo.** Round or D shaped links, wider than they are thick. Sturdy, holds a pendant well, and very common in British antique work.

**Curb.** Links twisted and flattened so they lie flat. Strong and comfortable. A heavier curb is a good everyday chain.

**Figaro.** A curb variant alternating one long link with two or three short ones. Popular from the mid twentieth century.

**Box.** Square links forming a smooth square profile. Elegant, but if it kinks it stays kinked, and a break is hard to repair invisibly.

**Rope.** Many small links spiralled together. Looks substantial and catches light well. Snaps at a weak point and is expensive to mend.

**Snake.** Tightly fitted plates forming a smooth tube. Beautiful and very prone to permanent kinking. Not a chain to be careless with.

**Trace.** A fine cable with small even links, often used for delicate pendants.

**Byzantine, Bismarck and similar.** Complex interwoven links. Heavy, expensive by weight, and striking.

**Longuard or muff chain.** A long antique chain, often 50 to 60 inches, made to be worn doubled or looped, frequently with a decorative dog clip. Victorian and Edwardian, and a genuine antique category rather than a modern style.

For daily wear with a pendant, curb, belcher and cable are the reliable choices. Rope, box and snake are the ones that come back broken.

## Clasps, and what they tell you about age

The clasp is often the most honest part of an old necklace.

- **Bolt ring**, a small spring loaded circle, is the common antique and vintage clasp. A worn one no longer springs shut and needs replacing.
- **Barrel or screw clasps** turn up on early twentieth century pieces.
- **Lobster clasps** are essentially modern. A lobster clasp on a supposedly Victorian chain means it has been replaced, which is normal and worth asking about.
- **Dog clips** on long guard chains are original to the type and not a repair.

A replaced clasp is not a fault, but on a hallmarked chain it matters: British chains were often marked on the clasp, so a replacement can take the hallmark with it. Our [guide to dating antique jewelry](/blog/how-to-date-antique-jewelry-by-era) covers findings as dating evidence.

## Weight matters more than length

A 20 inch chain can weigh 3 grams or 30. Weight tells you how substantial it will feel, how well it will wear, and what the metal alone is worth. Our [guide to resale value](/blog/how-much-is-my-jewelry-worth) explains how to turn weight and purity into a floor price.

A thin chain carrying a heavy pendant will fail, usually at the bail. Match the chain to the drop.

## Solid or hollow

Many modern chains, and some vintage ones, are hollow. They look substantial and weigh very little. Hollow chains kink permanently, are difficult to repair because heat collapses them, and are worth much less by weight.

Ask for the weight in grams and compare it to the length and width. If a wide, long chain weighs almost nothing, it is hollow.

## What to ask before you buy a chain second hand

- The exact length, measured flat from clasp end to clasp end.
- The weight in grams.
- The metal and where the mark is, with a photograph of it. [Gold marks](/blog/how-to-tell-if-gold-jewelry-is-real) and [silver marks](/blog/silver-hallmarks-explained) are both worth knowing.
- Whether the clasp is original.
- Whether there are any repaired or replaced links, and where.

On Nextmine, sellers set out length, metal and shipping on the listing, and the buyer's payment is held until the piece has arrived and been confirmed. [See the chains listed right now](/s), or read [how payments work](/how-payments-work).
`;

export default body;
